/**
 * 재사용 정렬 상태머신 + 비교 정렬 — Phaser/도메인 무관 순수 코어.
 *
 * 탭뷰 목록(보유 파츠 팔레트·합성/융합 목록 등)에 공통으로 쓰는 "정렬 키 선택 + 오름/내림 토글"을
 * 한 곳에 둔다. 도메인 타입은 제네릭 + keyExtractors로 주입 — 본 모듈은 어떤 row 타입도 알지 못한다.
 *
 * **다른 탭뷰에서 재사용하는 법:**
 * 1. 정렬 키 타입(`type MyKey = "a" | "b"`)과 키 목록·기본값을 도메인 파일에 정의한다.
 * 2. 각 키에서 비교값을 추출하는 `KeyExtractors<MyRow, MyKey>`를 구현한다.
 * 3. {@link SortFilterControl}에 keys/labelOf/onChange를 주입하면 드롭다운 + 방향 토글이 완성된다.
 * 4. {@link applySort}를 데이터 갱신 경로에 두면 정렬이 자동 반영된다.
 */

/** 정렬 방향. */
export type SortDir = "asc" | "desc";

/** 정렬 상태 — 선택된 키 + 방향. */
export interface SortState<K extends string> {
  readonly key: K;
  readonly dir: SortDir;
}

/**
 * 키별 비교값 추출기 맵 — number(수치 비교) 또는 string(localeCompare) 반환.
 * K의 각 키마다 row에서 비교값을 꺼내는 함수를 1:1로 매핑한다.
 */
export type KeyExtractors<T, K extends string> = Readonly<Record<K, (row: T) => number | string>>;

/**
 * 정렬 키 선택 이벤트 처리 — 같은 키 재선택이면 방향 토글, 다른 키면 그 키의 기본 방향으로 전환.
 * (드롭다운에서 "한 번 더 누르면 오름↔내림" UX의 순수 결정 로직.)
 *
 * @param prev 이전 정렬 상태.
 * @param picked 사용자가 선택한 키.
 * @param defaultDir 다른 키로 전환 시 적용할 기본 방향(기본 "asc").
 */
export function cycleOrToggle<K extends string>(
  prev: SortState<K>,
  picked: K,
  defaultDir: SortDir = "asc",
): SortState<K> {
  if (picked === prev.key) {
    return { key: picked, dir: prev.dir === "asc" ? "desc" : "asc" };
  }
  return { key: picked, dir: defaultDir };
}

/** 단일 비교값 비교 — number는 차, string은 localeCompare. */
function compareValues(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/**
 * rows를 정렬해 **새 배열**로 반환(원본 불변). desc는 비교 부호 반전.
 * 동률은 `tieBreak`(없으면 원본 순서 유지 — Array.sort는 ES2019+ 안정 정렬)으로 결정한다.
 */
export function applySort<T, K extends string>(
  rows: readonly T[],
  state: SortState<K>,
  extractors: KeyExtractors<T, K>,
  tieBreak?: (a: T, b: T) => number,
): T[] {
  const extract = extractors[state.key];
  const sign = state.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const cmp = compareValues(extract(a), extract(b));
    if (cmp !== 0) return cmp * sign;
    return tieBreak ? tieBreak(a, b) : 0;
  });
}
